import React from "react";
import { Stack, Box, Typography, Chip, Button } from "@mui/material";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import { formatDateInEasternTime } from "../../../utils/dateUtil/formatDateInEasternTime";
import { makeStoreDisplayName } from "../../../utils/dtoUtil/makeStoreDisplayName";
import {
  GfFlyerItemListDto,
  JobTestingRecord,
} from "@swagger/typescript-fetch-goflyer";
import { PermissionComponentWrapper } from "../../components/PermissionComponentWrapper";
import { MerchantLogo } from "./styled";

/**
 * Configuration properties for the MerchantInfoSection component.
 *
 * @interface MerchantInfoSectionProps
 */
interface MerchantInfoSectionProps {
  /**
   * Complete flyer item list data transfer object containing comprehensive merchant information,
   * associated store details, validity date range, and flyer generation metadata.
   *
   * @type {GfFlyerItemListDto | null | undefined}
   */
  flyerItemListData?: GfFlyerItemListDto | null;

  /**
   * Aggregate count of products or items contained within the current flyer item list.
   * This value represents the total number of items available for the promotional period.
   *
   * @type {number | undefined}
   */
  totalProducts?: number;

  /**
   * Controls the visibility of the total products count display.
   * When enabled, renders the product count metric alongside other flyer metadata.
   *
   * @type {boolean}
   * @default true
   */
  showTotalProducts?: boolean;

  /**
   * Indicates whether a smart flyer has been successfully generated for this item list.
   * When true, displays generation method badges (AI-generated or Figma-generated)
   * and enables access to the coordinate editor interface.
   *
   * @type {boolean}
   */
  hasFlyer?: boolean;

  /**
   * Optional callback handler invoked when the coordinate editor button is clicked.
   * This function should trigger the opening of the coordinate mapping editor modal,
   * allowing users to adjust product positioning on the generated flyer.
   *
   * @type {function(): void}
   */
  onOpenCoordinateEditor?: () => void;
}

/**
 * MerchantInfoSection Component
 *
 * A comprehensive information display component that presents merchant and store details
 * for a flyer item list. This section serves as the primary identification and metadata
 * display area within the flyer management interface.
 *
 * @component
 * @description
 * Renders a horizontally-oriented information panel featuring:
 * - Merchant logo (brand identifier)
 * - Store display name (derived from merchant and store data)
 * - Validity date range (start and end dates for the promotional period)
 * - Total product count (when enabled)
 * - Smart flyer generation badges (AI-generated or Figma-generated indicators)
 * - Coordinate editor access button (when flyer exists and user has permissions)
 *
 * The component implements responsive design patterns with adaptive typography and
 * layout adjustments across different viewport sizes (xs, sm, md, lg).
 *
 * @param {MerchantInfoSectionProps} props - Component configuration properties
 * @param {GfFlyerItemListDto | null} [props.flyerItemListData] - Flyer data containing merchant, store, and date range information
 * @param {number} [props.totalProducts] - Total count of products in the flyer item list
 * @param {boolean} [props.showTotalProducts=true] - Flag to control product count display visibility
 * @param {boolean} [props.hasFlyer=false] - Indicates whether a smart flyer has been generated
 * @param {function} [props.onOpenCoordinateEditor] - Callback to launch the coordinate editor modal
 *
 * @returns {React.ReactElement | null} Rendered merchant information section, or null if flyerItemListData is not provided
 *
 * @example
 * ```tsx
 * <MerchantInfoSection
 *   flyerItemListData={flyerData}
 *   totalProducts={42}
 *   showTotalProducts={true}
 *   hasFlyer={true}
 *   onOpenCoordinateEditor={handleOpenEditor}
 * />
 * ```
 */
const MerchantInfoSection: React.FC<MerchantInfoSectionProps> = ({
  flyerItemListData,
  totalProducts,
  showTotalProducts = true,
  hasFlyer = false,
  onOpenCoordinateEditor,
}) => {
  const { t } = useTranslation();
  const smartFlyer = flyerItemListData?.flyer;
  const aiGenerated =
    flyerItemListData?.jobTestingRecord ===
    JobTestingRecord.AICOORDINATEGENERATION;

  if (!flyerItemListData) return null;

  return (
    <Stack
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{ minWidth: 0, width: "100%" }}
      data-testid="merchant-info-section"
    >
      <Box sx={{ flexShrink: 0 }}>
        <MerchantLogo
          src={flyerItemListData.stores[0]?.merchant?.merchantLogo?.src}
        />
      </Box>
      <Stack sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="h6"
          sx={{
            fontSize: {
              xs: "14px",
              sm: "16px",
              md: "18px",
              lg: "20px",
            },
            fontWeight: 600,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            lineHeight: 1.3,
            mb: 0.5,
          }}
          data-testid="create-flyer-item-merchant-name"
        >
          {makeStoreDisplayName(flyerItemListData.stores[0])}
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.5, sm: 1 },
            flexWrap: "wrap",
          }}
        >
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{
              fontSize: { xs: "10px", sm: "11px" },
              color: "#ff8038",
              fontWeight: 600,
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: "inherit",
                fontWeight: "inherit",
                color: "inherit",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {t(messages.Start_Date())}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize: { xs: "11px", sm: "12px" },
                fontWeight: 600,
                color: "text.primary",
              }}
            >
              {formatDateInEasternTime(flyerItemListData?.validStartDate)}
            </Typography>
          </Stack>
          <Typography
            component="span"
            sx={{
              fontSize: { xs: "10px", sm: "11px" },
              color: "text.secondary",
              fontWeight: 400,
            }}
          >
            —
          </Typography>
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{
              fontSize: { xs: "10px", sm: "11px" },
              color: "#ff8038",
              fontWeight: 600,
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: "inherit",
                fontWeight: "inherit",
                color: "inherit",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {t(messages.End_Date())}
            </Typography>
            <Typography
              component="span"
              sx={{
                fontSize: { xs: "11px", sm: "12px" },
                fontWeight: 600,
                color: "text.primary",
              }}
            >
              {formatDateInEasternTime(flyerItemListData?.validEndDate)}
            </Typography>
          </Stack>
        </Box>
        {(showTotalProducts || hasFlyer) && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ mt: 0.5, flexWrap: "wrap", rowGap: 0.75 }}
          >
            {showTotalProducts && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: "11px", sm: "12px" },
                  fontWeight: 600,
                  color: "text.secondary",
                  lineHeight: 1.3,
                }}
              >
                {t(messages.TOTAL_PRODUCTS())}: {totalProducts ?? 0}
              </Typography>
            )}

            {smartFlyer && (
              <Chip
                label={
                  aiGenerated
                    ? t(messages.AI_Generated())
                    : t(messages.Figma_Generated())
                }
                size="small"
                sx={{
                  fontSize: "9px",
                  height: "18px",
                  mt: 0.5,
                  backgroundColor: "#03b7cf",
                  color: "#fff",
                  borderRadius: "4px",
                  maxWidth: "fit-content",
                }}
              />
            )}
            {aiGenerated && (
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: "10px", sm: "11px" },
                  fontWeight: 600,
                  color: "text.secondary",
                  lineHeight: 1.3,
                }}
                data-testid="merchant-info-ai-generation-model"
              >
                {t(messages.AI_Generation_Model())}:{" "}
                {flyerItemListData.aiGenerationModel ?? "—"}
              </Typography>
            )}
            {hasFlyer && onOpenCoordinateEditor ? (
              <Box sx={{ flexShrink: 0 }}>
                <PermissionComponentWrapper
                  requiredPermission={["review"]}
                  requiredPermissionGroup={["smartfly"]}
                >
                  <Button
                    data-testid="coordinate-editor-button"
                    variant="contained"
                    onClick={onOpenCoordinateEditor}
                    sx={{
                      backgroundColor: "#ff8a3d",
                      color: "white",
                      fontWeight: 700,
                      textTransform: "none",
                      borderRadius: "4px",
                      padding: { xs: "6px 10px", sm: "7px 14px" },
                      fontSize: { xs: "12px", sm: "13px" },
                      lineHeight: 1.1,
                      transition: "all 0.18s ease-in-out",
                      whiteSpace: "nowrap",
                      height: "25px",
                      "&:hover": {
                        backgroundColor: "#ffa048",
                        boxShadow: "0 5px 14px rgba(255, 122, 42, 0.24)",
                        transform: "translateY(-0.5px)",
                      },
                      "&:active": {
                        transform: "translateY(0.5px)",
                        boxShadow: "0 3px 10px 0 rgba(255, 122, 42, 0.18)",
                      },
                    }}
                    startIcon={
                      <MapOutlinedIcon
                        sx={{ fontSize: { xs: 18, sm: 20 }, color: "white" }}
                      />
                    }
                  >
                    Coord. Editor
                  </Button>
                </PermissionComponentWrapper>
              </Box>
            ) : null}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default MerchantInfoSection;
